import React, { useEffect, useState } from "react";
import type { RouteComponentProps } from "react-router-dom";

import { getQueryParams, uuidv4 } from "@/utils/filters";
import images from "@/utils/images";
import { workflowRequests } from "@/api/(requests)/workflow";
import { workflowTokenStore } from "@/api/helpers/workflowTokenStore";

import AccountLookup from "./(eligibility)/AccountLookup";
import ChooseNewOrExistingMemberWorkflow from "./ChooseNewOrExistingMemberWorkflow/ChooseNewOrExistingMemberWorkflow";
import ConfirmIdentity from "./(confirmation)/ConfirmIdentity";
import ConfirmInsurance from "./(confirmation)/ConfirmInsurance";
import DemographicEligibilityCheck from "./(eligibility)/DemographicEligibilityCheck";
import EnterOTC from "./(auth)/EnterOTC";
import GenericWorkflowScreening from "./Screening/GenericWorkflowScreening";
import Ineligible from "./(terminal)/Ineligible";
import IntakeScheduling from "./IntakeScheduling/IntakeScheduling";
import MemberAuth from "./(auth)/MemberAuth";
import PracticeFailure from "./(terminal)/PracticeFailure";

import "./OnboardingWorkflow.styles.scss";

type WorkflowInstance = {
  current_node_name: Nullable<string>;
  entity_id: string;
  first_node: string;
  previous_workflow?: boolean;
  sort_key: string;
};

type SharedWorkflowData = {
  workflow_instance?: WorkflowInstance;
};

type GetWorkflowResponse = {
  entity_id: string;
  workflow_name: string;
};

workflowTokenStore.clear();

// Renders the current step of the member-facing signup workflow. Mounted directly
// (unauthenticated entry) or via OnboardingRouter (authenticated system:workflow role).
export function OnboardingWorkflow(props: RouteComponentProps) {
  const [sharedWorkflowData, setSharedWorkflowData] = useState<SharedWorkflowData>({});
  const [currentStepName, setCurrentStepName] = useState<Nullable<string>>(null);
  const practiceId = getQueryParams("practiceId", props.location.search);

  const backHandler = async () => {
    const data = {
      workflow_instance_entity_id: sharedWorkflowData.workflow_instance!.entity_id,
      workflow_instance_sort_key: sharedWorkflowData.workflow_instance!.sort_key,
    };
    const headers = {
      "X-On-Behalf-Of": sharedWorkflowData.workflow_instance!.entity_id,
    };
    const response = (await workflowRequests.undo_state_and_backtrack_one_node({
      data,
      headers,
    })) as { workflow_instance: WorkflowInstance };

    if (
      response.workflow_instance.first_node === response.workflow_instance.current_node_name &&
      !response.workflow_instance.previous_workflow
    ) {
      resetWorkflow();
    } else {
      setSharedWorkflowData({
        ...sharedWorkflowData,
        workflow_instance: response.workflow_instance,
      });
    }
  };

  const resetWorkflow = () => {
    window.location.reload();
  };

  const baseProps = {
    backHandler,
    resetWorkflow,
    setSharedWorkflowData,
    sharedWorkflowData,
  };

  const workflowSteps: Record<string, React.ReactNode> = {
    account_lookup: <AccountLookup {...baseProps} />,
    choose_workflow: <ChooseNewOrExistingMemberWorkflow {...baseProps} />,
    confirm_identity: <ConfirmIdentity {...baseProps} />,
    confirm_insurance: <ConfirmInsurance {...baseProps} />,
    demographic_data: <DemographicEligibilityCheck {...baseProps} />,
    ineligible: <Ineligible {...baseProps} />,
    member_auth: <MemberAuth {...baseProps} />,
    OTC: <EnterOTC {...baseProps} />,
    practice_failure: <PracticeFailure />,
    schedule_appointment: <IntakeScheduling {...baseProps} />,
    screening: <GenericWorkflowScreening {...baseProps} />,
  };

  useEffect(() => {
    (async () => {
      if (!currentStepName) {
        const data = { practice_id: practiceId };
        const workflowResponse = (await workflowRequests.get_workflow({
          data,
        })) as Nullable<GetWorkflowResponse>;

        if (workflowResponse) {
          const data = {
            entity_type: "session",
            workflow_config_entity_id: workflowResponse.entity_id,
            workflow_instance_entity_id: uuidv4(),
            workflow_name: workflowResponse.workflow_name,
          };
          const response = (await workflowRequests.create_workflow_instance({
            data,
          })) as WorkflowInstance;

          setSharedWorkflowData({ ...sharedWorkflowData, workflow_instance: response });
        }
      } else if (
        !sharedWorkflowData.workflow_instance ||
        (currentStepName && !Object.hasOwn(workflowSteps, currentStepName))
      ) {
        setCurrentStepName(null);
        props.history.push(`/app/workflow?practiceId=${practiceId}`);
      }
    })();
    // oxlint-disable-next-line  eslint-plugin-react-hooks(exhaustive-deps))
  }, [currentStepName]);

  //display workflow step based on changes to workflow instance
  useEffect(
    () => {
      let stepName =
        sharedWorkflowData.workflow_instance &&
        sharedWorkflowData.workflow_instance.current_node_name;

      if (sharedWorkflowData.workflow_instance && !stepName) {
        stepName = sharedWorkflowData.workflow_instance.first_node;
      }

      if (stepName) {
        setCurrentStepName(stepName);
        props.history.push(`/app/workflow?practiceId=${practiceId}&step=${stepName}`);
      }
    },

    // oxlint-disable-next-line  eslint-plugin-react-hooks(exhaustive-deps))
    [sharedWorkflowData.workflow_instance],
  );

  //display workflow step based on changes to nav buttons (go back/forward)
  useEffect(
    () => {
      const stepName = getQueryParams("step", props.location.search);

      if (
        !sharedWorkflowData.workflow_instance ||
        (stepName &&
          stepName !== currentStepName &&
          stepName === sharedWorkflowData.workflow_instance.first_node)
      ) {
        setCurrentStepName(null);
        setSharedWorkflowData({});
        props.history.push(`/app/workflow?practiceId=${practiceId}`);
      } else if (
        stepName &&
        stepName !== currentStepName &&
        stepName !== sharedWorkflowData.workflow_instance.first_node
      ) {
        setCurrentStepName(stepName);
        props.history.push(`/app/workflow?practiceId=${practiceId}&step=${stepName}`);
      }
    },

    // oxlint-disable-next-line  eslint-plugin-react-hooks(exhaustive-deps))
    [getQueryParams("step", props.location.search)],
  );

  return (
    <div className="self-signup-content" style={{ overflowX: "hidden" }}>
      <div>
        <header className="brand-header" style={{ position: "relative" }}>
          <div className="brand-header-logo">
            <span className="logo-and-insurance" style={{ float: "none", margin: " auto" }}>
              <img
                alt="aptihealth"
                className="apti-img"
                src={images("./common/apti_logo_w_tagline.svg")}
              />
            </span>
          </div>
        </header>

        {currentStepName &&
          Object.hasOwn(workflowSteps, currentStepName) &&
          workflowSteps[currentStepName]}
      </div>
    </div>
  );
}
